import { useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { getLinkWithLocale, getSecureUploadLink } from "../../utils/path-helper";
import { currentDomain } from "../../utils/general-config";
import { DomainEnum } from "../../services/enum/environment-enum";
import { getSignSimpliBaseURL } from "../../services/api-constants";
import { deleteCookie, getQueryParam, readCookie } from "../../utils/helpers";
import { EnvelopeEvent, GetEnvelopeByEnvelopeId, MultiSignerEnvelopeEvent } from "../../utils/document-helper";
import { SignerType } from "../../utils/enum-helper";
// --- Sub-Components for Different States ---

/**
 * Renders the state where email is verified, but phone verification is pending.
 * Corresponds to image_2811af.png.
 */
const VerificationSetupView = ({ isEmailVerified, onVerifyPhone }) => {
  return (
    <>
      <div className="flex justify-center mb-4">
        <div className="flex items-center justify-center w-[40px] h-[40px] md:w-[70px] md:h-[70px] bg-[#E7F1FF] rounded-[16.8px]">
          <img
            className="check-icon p-2"
            src={process.env.PUBLIC_URL + "/static/images/protect-icon-blue.svg"}
            alt="check icon"
          />
        </div>
      </div>

      <p className="mt-2 text-center text-md text-gray-700">
        2-step email and phone verification is
        <br /> required to protect your account
      </p>

      <div className="flex justify-center gap-4 mt-6">
        {/* Verify by email */}
        <div className="relative p-4 border border-gray-200 rounded-lg w-40 text-center shadow-sm">
          {isEmailVerified && (
            <div className="flex flex-col justify-center items-center absolute top-[-8px] right-[-8px] w-[35px;] h-[35px;] bg-[#17AE06] rounded-full">
              <svg xmlns="http://www.w3.org/2000/svg" width="25" height="14" viewBox="0 0 25 14" fill="none">
                <path d="M10.3301 7.32469L14.051 11.0456L23.1464 1.95009" stroke="#EAEBED" strokeWidth="2.89333" />
                <path d="M1.85352 5.87768L6.81469 11.0456L15.9102 1.95009" stroke="white" strokeWidth="2.89333" />
              </svg>
            </div>
          )}
          <div className="bg-[#E7F1FF] rounded-[12px] w-[40px] h-[40px] md:w-[50px] md:h-[50px] flex items-center justify-center mx-auto mb-2">
            <img
              className="check-icon"
              src={process.env.PUBLIC_URL + "/static/images/email-icon-blue.svg"}
              alt="check icon"
            />
          </div>
          <p className="text-sm">Verify by email</p>
        </div>

        {/* Verify by phone (Pending) */}
        <div className="p-4 border border-gray-200 rounded-lg w-40 text-center text-gray-500 shadow-sm">
          <div className="bg-[#E7F1FF] rounded-[12px] w-[40px] h-[40px] md:w-[50px] md:h-[50px] flex items-center justify-center mx-auto mb-2">
            <img
              className="check-icon"
              src={process.env.PUBLIC_URL + "/static/images/phone-icon-blue.svg"}
              alt="check icon"
            />
          </div>
          <p className="text-sm">Verify by phone</p>
        </div>
      </div>

      {/* Verify Phone Button */}
      <div className="mt-8 flex justify-center">
        <button
          type="button"
          onClick={() => onVerifyPhone("phone")}
          className="w-full max-w-xs py-3 text-white text-lg font-bold rounded-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 transition-all duration-300"
        >
          VERIFY PHONE
        </button>
      </div>
    </>
  );
};

/**
 * Renders the state where both email and phone are verified.
 * Corresponds to image_2815ab.png.
 */
const VerificationCompleteView = ({ onContinue, multiFactorLevel }) => {
  const isSignSimpli = currentDomain() === DomainEnum.SIGNSIMPLI;
  return (
    <>
      <div className="flex justify-center mb-4">
        {/* SecureSimplii Logo - assuming this is the full W logo */}
        <div className="flex items-center justify-center w-14 h-14 rounded-full">
          {isSignSimpli ? (
            <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="60" height="60" rx="30" fill="#1D7FFF" />
              <path d="M26.2793 31.4171L32.6579 37.7957L48.2502 22.2035" stroke="#EAEBED" strokeWidth="4.96" />
              <path d="M11.75 28.9372L20.2549 37.7965L35.8471 22.2042" stroke="white" strokeWidth="4.96" />
            </svg>
          ) : (
            <img
              className="check-icon"
              src={process.env.PUBLIC_URL + "/static/images/double-check-circle-green.svg"}
              width="180"
              alt="check icon"
            />
          )}
        </div>
      </div>

      <h2 className="text-center text-xl font-semibold text-gray-900">Verification complete</h2>

      <p className="mt-2 text-center text-sm text-gray-700">Your account is now secured with 2-step verification</p>

      <div className="flex justify-center gap-4 mt-6">
        {/* Verify by email (Complete) */}
        <div className="relative p-4 border border-gray-200 rounded-lg w-40 text-center shadow-sm">
          <div className="flex flex-col justify-center items-center absolute top-[-8px] right-[-8px] w-[35px;] h-[35px;] bg-[#17AE06] rounded-full">
            <svg xmlns="http://www.w3.org/2000/svg" width="25" height="14" viewBox="0 0 25 14" fill="none">
              <path d="M10.3301 7.32469L14.051 11.0456L23.1464 1.95009" stroke="#EAEBED" strokeWidth="2.89333" />
              <path d="M1.85352 5.87768L6.81469 11.0456L15.9102 1.95009" stroke="white" strokeWidth="2.89333" />
            </svg>
          </div>
          <div className="bg-[#E7F1FF] rounded-[12px] w-[40px] h-[40px] md:w-[50px] md:h-[50px] flex items-center justify-center mx-auto mb-2">
            <img
              className="check-icon"
              src={process.env.PUBLIC_URL + "/static/images/email-icon-blue.svg"}
              alt="check icon"
            />
          </div>
          <p className="text-sm font-bold">Verify by email</p>
        </div>

        {/* Verify by phone (Complete) */}
        {multiFactorLevel !== 1 && (
          <div className="relative p-4 border border-gray-200 rounded-lg w-40 text-center shadow-sm">
            <div className="flex flex-col justify-center items-center absolute top-[-8px] right-[-8px] w-[35px;] h-[35px;] bg-[#17AE06] rounded-full">
              <svg xmlns="http://www.w3.org/2000/svg" width="25" height="14" viewBox="0 0 25 14" fill="none">
                <path d="M10.3301 7.32469L14.051 11.0456L23.1464 1.95009" stroke="#EAEBED" strokeWidth="2.89333" />
                <path d="M1.85352 5.87768L6.81469 11.0456L15.9102 1.95009" stroke="white" strokeWidth="2.89333" />
              </svg>
            </div>
            <div className="bg-[#E7F1FF] rounded-[12px] w-[40px] h-[40px] md:w-[50px] md:h-[50px] flex items-center justify-center mx-auto mb-2">
              <img
                className="check-icon"
                src={process.env.PUBLIC_URL + "/static/images/phone-icon-blue.svg"}
                alt="check icon"
              />
            </div>
            <p className="text-sm font-bold">Verify by phone</p>
          </div>
        )}
      </div>

      {/* Continue to Documents Button */}
      <div className="mt-8 flex justify-center">
        <button
          type="button"
          onClick={onContinue}
          className="w-full max-w-xs py-3 text-white text-lg font-bold rounded-full bg-orange-500 hover:bg-orange-600 transition-colors duration-300"
        >
          CONTINUE TO DOCUMENTS
        </button>
      </div>
    </>
  );
};

/**
 * Main component to manage the flow and render the correct view.
 * Simulates the entire verification process using state.
 */
const TwoStepVerification = ({
  toggleModal,
  isEmailVerified,
  isPhoneVerified,
  onVerifyPhone,
  togglePsscVerificationModal,
  multiFactorLevel,
}) => {
  const isSignSimpli = currentDomain() === DomainEnum.SIGNSIMPLI;
  const logoSrc =
    isSignSimpli
      ? "/static/images/logos/signsimpli-new-logo.svg"
      : "/static/images/logos/pdfsimpli-new-logo.svg";
  // Use state to track the verification status of email and phonefault to false
  let pathRedirect = getSecureUploadLink(null, "");
  const navigate = useNavigate();
  // Derived state to determine which view to show

  if (multiFactorLevel === 1) {
    isEmailVerified = true;
    isPhoneVerified = true;
  }

  const isComplete = isEmailVerified && isPhoneVerified;

  const handleVerifyPhone = () => {
    onVerifyPhone(); // Calls the parent function to change the main modal step to 'VERIFY_PHONE'
    console.log("Starting phone verification process.");
  };

  const handleContinue = async () => {
    if (currentDomain() === DomainEnum.SIGNSIMPLI) {
      const paymentUrl =
      getSignSimpliBaseURL + "/app" + getLinkWithLocale(null, "billing/plans");

      try {
        const envId = getQueryParam("evp");
        const flValue = readCookie("fl")?.toString().trim().toLowerCase();

        // 👉 Case 1: fl === "true"
        if (flValue === "true") {
          deleteCookie("fl");
          window.location.assign(paymentUrl);
          return; // 🔴 stop everything
        }

      // 👉 Case 2: normal flow
      if (envId) {
        const envelope = await GetEnvelopeByEnvelopeId(envId);
        const signerType = envelope?.signerType ?? null;

         if (signerType === SignerType.MULTISIGNER) {
            await MultiSignerEnvelopeEvent(signerType, envId);
          } else{
            await EnvelopeEvent(signerType, envId);
          }
      }

      } catch (err) {
      console.error("Error in envelope handling flow:", err);
      }

      // 👉 Single redirect point (VERY IMPORTANT)
      deleteCookie("fl");
      window.location.assign(paymentUrl);
      return;
    } else {
      console.log("Navigating to documents...", pathRedirect);
      navigate(pathRedirect);
      togglePsscVerificationModal(false);
    }
};

  return (
    <div className="">
      <div className="border-b border-gray-200 relative">
        {/* Close Button */}
        {!isSignSimpli && (
          <div className="absolute top-0 right-0 pt-4 pr-4 z-20">
            <button
              type="button"
              className="rounded-md text-gray-400 hover:text-gray-600"
              onClick={() => toggleModal(false)}
            >
              <span className="sr-only">Close</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
        )}
        <div className="flex pl-6 py-2">
          <img
            className="pdf-simpli-logo flex w-36 sm:w-40 lg:w-44 h-7 sm:h-9 lg:h-10"
            src={process.env.PUBLIC_URL + logoSrc}
            width="180"
            alt="Logo"
          />
        </div>
      </div>

      <div className="">
        <div className="px-6 pt-6 md:pb-6">
          {isComplete ? (
            <VerificationCompleteView onContinue={handleContinue} multiFactorLevel={multiFactorLevel} />
          ) : (
            <VerificationSetupView isEmailVerified={isEmailVerified} onVerifyPhone={onVerifyPhone} />
          )}
        </div>

        {/* Encrypted and Secure Footer */}
        <div className="py-4 text-center text-xs text-gray-500 md:border-t md:border-gray-200">
          <span className="inline-flex items-center">
            {/* Lock icon placeholder */}
            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="12" viewBox="0 0 10 12" fill="none">
              <path
                d="M8.37483 5.33217H7.85893V3.7845C7.85893 3.19695 7.71205 2.6524 7.41827 2.15083C7.1245 1.64927 6.72683 1.25161 6.22527 0.957832C5.72371 0.66406 5.17916 0.517174 4.59161 0.517174C4.00407 0.517174 3.45951 0.66406 2.95795 0.957832C2.45639 1.25161 2.05872 1.64927 1.76495 2.15083C1.47118 2.6524 1.32429 3.19695 1.32429 3.7845V5.33217H0.808397C0.52179 5.33217 0.278174 5.43249 0.0775488 5.63311C-0.123076 5.83374 -0.223389 6.07735 -0.223389 6.36396V10.4911C-0.223389 10.7777 -0.123076 11.0213 0.0775488 11.222C0.278174 11.4226 0.52179 11.5229 0.808397 11.5229H8.37483C8.66143 11.5229 8.90505 11.4226 9.10567 11.222C9.3063 11.0213 9.40661 10.7777 9.40661 10.4911V6.36396C9.40661 6.07735 9.3063 5.83374 9.10567 5.63311C8.90505 5.43249 8.66143 5.33217 8.37483 5.33217ZM6.13929 5.33217H3.04393V3.7845C3.04393 3.35458 3.1944 2.98916 3.49534 2.68822C3.79628 2.38729 4.1617 2.23682 4.59161 2.23682C5.02152 2.23682 5.38695 2.38729 5.68788 2.68822C5.98882 2.98916 6.13929 3.35458 6.13929 3.7845V5.33217Z"
                fill="#64C897"
              />
            </svg>
            <span className="ml-2 text-[#16283D] text-[10px]">Your information is encrypted and secure</span>
          </span>
        </div>
      </div>
    </div>
  );
};

export default TwoStepVerification;
